<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    evitar-banner-mobile
    @lang-change="onLangChange"
  >
    <!-- ============================================================
         HERO — contexto da página (quem confia em OligoCheck)
         ============================================================ -->
    <PageHero
      :logo-src="logoBranco"
      logo-alt="OligoCheck"
      :badge="t.heroBadge"
      badge-variante="azul"
      :subtitulo="t.heroSubtitulo"
    >
      <template #titulo>
        <span class="titulo-resto">{{ t.heroTituloAntes + ' ' }}</span
        ><span class="ds-texto-gradiente">{{ t.heroTituloDestaque }}</span>
      </template>

      <template #ctas>
        <div class="trust-inline">
          <TrustBar
            :items="[
              { texto: t.trust1, icone: 'users' },
              { texto: t.trust2, icone: 'shield' },
              { texto: t.trust3, icone: 'clock' }
            ]"
          />
        </div>
      </template>
    </PageHero>

    <!-- ============================================================
         CARROSSEL DE PROFISSIONAIS
         Marquee horizontal infinito com avatares + nome + especialidade.
         ============================================================ -->
    <SecaoPadrao id="profissionais" variante="alt" container="none">
      <div class="ds-container-sm profissionais-header">
        <SecaoHeader
          :badge="t.profBadge"
          badge-variante="azul"
          :titulo-antes="t.profTituloAntes"
          :titulo-destaque="t.profTituloDestaque"
          :subtitulo="t.profSubtitulo"
        />
      </div>

      <div
        class="marquee"
        :aria-label="t.profAria"
        @mouseenter="pausaMarquee = true"
        @mouseleave="pausaMarquee = false"
      >
        <div class="marquee-track" :class="{ 'marquee-track--paused': pausaMarquee }">
          <div
            v-for="(prof, i) in profissionaisLoop"
            :key="i"
            class="prof-card"
            :aria-hidden="i >= t.profissionais.length ? 'true' : null"
          >
            <div class="prof-topo">
              <div class="prof-avatar" :style="{ background: prof.gradient }">
                <span>{{ prof.iniciais }}</span>
              </div>
              <div class="prof-info">
                <strong>{{ prof.nome }}</strong>
                <span>{{ prof.role }}</span>
              </div>
            </div>
            <img
              class="prof-foto"
              :src="prof.foto"
              :alt="`${prof.nome} usando OligoCheck`"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
        <div class="marquee-fade marquee-fade--left" aria-hidden="true"></div>
        <div class="marquee-fade marquee-fade--right" aria-hidden="true"></div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         DEPOIMENTOS EM VÍDEO
         Player principal + lista de miniaturas (clicar troca o player).
         ============================================================ -->
    <SecaoPadrao id="videos" container="sm">
      <SecaoHeader
        :badge="t.depBadge"
        badge-variante="azul"
        :titulo-antes="t.depTituloAntes"
        :titulo-destaque="t.depTituloDestaque"
        :subtitulo="t.depSubtitulo"
      />

      <!-- Player principal -->
      <div class="player-principal">
        <div class="video-wrapper">
          <iframe
            :key="videoAtivo.videoId"
            :src="playerUrl"
            :title="`${videoAtivo.autor} — ${videoAtivo.role}`"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>

        <div class="player-info">
          <div class="player-info-ident">
            <strong class="player-autor">{{ videoAtivo.autor }}</strong>
            <span class="player-role">{{ videoAtivo.role }}</span>
          </div>

          <div class="player-controles">
            <button
              type="button"
              class="player-btn"
              :aria-label="t.labelAnterior"
              @click="anteriorVideo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <span class="player-contador" aria-live="polite">
              {{ videoAtivoIndex + 1 }} / {{ t.depoimentos.length }}
            </span>
            <button
              type="button"
              class="player-btn"
              :aria-label="t.labelProximo"
              @click="proximoVideo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Navegação rápida de vídeos -->
      <div
        ref="depNavRef"
        class="dep-nav"
        role="tablist"
        :aria-label="t.depNavAria"
      >
        <button
          v-for="(dep, index) in t.depoimentos"
          :key="dep.videoId + index"
          role="tab"
          type="button"
          class="dep-thumb"
          :class="{ 'dep-thumb--ativo': index === videoAtivoIndex }"
          :aria-selected="index === videoAtivoIndex"
          :aria-label="`${dep.autor} — ${dep.role}`"
          @click="selecionarVideo(index)"
        >
          <div class="dep-thumb-img">
            <img
              :src="`https://i.ytimg.com/vi/${dep.videoId}/hqdefault.jpg`"
              :alt="dep.autor"
              loading="lazy"
              width="320"
              height="180"
            />
            <span class="dep-thumb-play" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="6 4 20 12 6 20 6 4" />
              </svg>
            </span>
          </div>
          <div class="dep-thumb-info">
            <strong>{{ dep.autor }}</strong>
            <span>{{ dep.role }}</span>
          </div>
        </button>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         CTA — “¿Quieres ser el próximo?”
         ============================================================ -->
    <SecaoPadrao id="cta" variante="alt" container="sm">
      <div class="cta-bloco">
        <SecaoHeader
          :badge="t.ctaBadge"
          badge-variante="destaque"
          :titulo-antes="t.ctaTituloAntes"
          :titulo-destaque="t.ctaTituloDestaque"
          :subtitulo="t.ctaSubtitulo"
        />

        <a
          :href="whatsappUrl"
          target="_blank"
          rel="noopener"
          class="caminho-card caminho-cta"
        >
          <div class="caminho-icone icone-cta">
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
            </svg>
          </div>
          <div class="caminho-conteudo">
            <span class="caminho-label">{{ t.ctaCardLabel }}</span>
            <strong class="caminho-titulo">{{ t.ctaCardTitulo }}</strong>
            <span class="caminho-desc">{{ t.ctaCardDesc }}</span>
          </div>
          <div class="caminho-seta">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </div>
        </a>
      </div>
    </SecaoPadrao>

    <template #rodape>
      <RodapeSite marca="OligoCheck" />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import PageHero from '@/components/global/layout/PageHero.vue'
import SecaoPadrao from '@/components/global/secao/SecaoPadrao.vue'
import SecaoHeader from '@/components/global/secao/SecaoHeader.vue'
import TrustBar from '@/components/global/ui/TrustBar.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import logoBranco from '@/assets/images/logo-branco.svg'
import { useSeo } from '@/composables/useSeo'

const currentLang = ref('es')
const pausaMarquee = ref(false)
const videoAtivoIndex = ref(0)
const depNavRef = ref(null)

const onLangChange = () => {
  /* AppShell já controla a escolha manual internamente. */
}

/* ============================================================
   Troca de vídeo no player principal
   autoplay=1 é intencional ao trocar (UX: o usuário clicou
   esperando assistir, sem precisar clicar play de novo).

   Após trocar, centraliza a miniatura ativa na lista horizontal
   (importante no mobile, onde a nav é um scroll lateral).
   ============================================================ */
const selecionarVideo = (index) => {
  videoAtivoIndex.value = index
  nextTick(() => centralizarMiniaturaAtiva(index))
}

const centralizarMiniaturaAtiva = (index) => {
  const nav = depNavRef.value
  if (!nav) return
  const item = nav.children?.[index]
  if (item && typeof item.scrollIntoView === 'function') {
    item.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }
}

const anteriorVideo = () => {
  const total = t.value.depoimentos.length
  selecionarVideo((videoAtivoIndex.value - 1 + total) % total)
}

const proximoVideo = () => {
  const total = t.value.depoimentos.length
  selecionarVideo((videoAtivoIndex.value + 1) % total)
}

const videoAtivo = computed(() => t.value.depoimentos[videoAtivoIndex.value])

const playerUrl = computed(() => {
  const id = videoAtivo.value.videoId
  const autoplay = videoAtivoIndex.value === 0 ? 0 : 1
  return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&autoplay=${autoplay}`
})

/* ============================================================
   Lista duplicada do carrossel (loop infinito visual)
   ============================================================ */
const profissionaisLoop = computed(() => [
  ...t.value.profissionais,
  ...t.value.profissionais
])

/* ============================================================
   WhatsApp — quero ser o próximo
   ============================================================ */
const whatsappFone = '34617488404'
const whatsappUrl = computed(() => {
  const msgs = {
    es: 'Hola! Vi los testimonios de OligoCheck y me gustaría tener más información.',
    pt: 'Olá! Vi os depoimentos do OligoCheck e gostaria de ter mais informações.',
    en: "Hi! I saw the OligoCheck testimonials and I'd like to get more information."
  }
  const msg = encodeURIComponent(msgs[currentLang.value])
  return `https://api.whatsapp.com/send/?phone=${whatsappFone}&text=${msg}`
})

/* ============================================================
   Catálogo compartilhado: ids de vídeo + gradientes de avatar.
   O texto (autor, role) é traduzido em `translations` abaixo.

   Para adicionar/alterar depoimentos: edite os blocos `depoimentos`
   dentro de cada idioma. IDs de vídeo são do YouTube (embed IDs).
   ============================================================ */
const GRADIENTES = [
  'linear-gradient(135deg, #14cbf0, #0fa7c5)',
  'linear-gradient(135deg, #1aaf95, #0f8474)',
  'linear-gradient(135deg, #f58220, #c96107)',
  'linear-gradient(135deg, #a855f7, #7b2bd1)',
  'linear-gradient(135deg, #ff4d79, #c41e6b)',
  'linear-gradient(135deg, #22c55e, #15803d)',
  'linear-gradient(135deg, #f43f5e, #9f1239)',
  'linear-gradient(135deg, #3b82f6, #1d4ed8)',
  'linear-gradient(135deg, #eab308, #a16207)',
  'linear-gradient(135deg, #06b6d4, #0e7490)'
]

const gradiente = (i) => GRADIENTES[i % GRADIENTES.length]

/* ============================================================
   Fotos reais dos profissionais no carrossel (CDN DigitalOcean).
   Usadas ciclicamente caso tenhamos mais cartões que fotos.
   Para trocar/adicionar, basta editar esta lista.
   ============================================================ */
const PROF_FOTOS = [
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/car1.jpg',
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/car6.PNG',
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/car2.jpg',
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/car3.jpg',
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/car5.PNG',
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/car5.PNG'
]

const foto = (i) => PROF_FOTOS[i % PROF_FOTOS.length]

/* ============================================================
   i18n
   ============================================================ */
const translations = {
  /* ---------------------- ESPAÑOL ---------------------- */
  es: {
    heroBadge: 'Testimonios · Área profesional',
    heroTituloAntes: 'Lo que dicen',
    heroTituloDestaque: 'quienes ya usan OligoCheck',
    heroSubtitulo:
      'Profesionales de la salud de todo el mundo comparten cómo el análisis mineral intracelular en 5 segundos cambió la práctica de su consulta.',
    trust1: '+12.000 profesionales',
    trust2: 'Casos reales verificados',
    trust3: '+10 años en el mercado',

    profBadge: 'Red OligoCheck',
    profTituloAntes: 'Profesionales que',
    profTituloDestaque: 'confían en OligoCheck',
    profSubtitulo: 'Un recorrido por la comunidad de médicos, nutricionistas y terapeutas que usan OligoCheck en su día a día.',
    profAria: 'Carrusel de profesionales que usan OligoCheck',
    profissionais: [
      { nome: 'Dra. Elena Torres',   role: 'Nutricionista · Madrid',                     iniciais: 'ET', gradient: gradiente(0), foto: foto(0) },
      { nome: 'Dr. Marco Rossi',     role: 'Médico Integrativo · Barcelona',             iniciais: 'MR', gradient: gradiente(1), foto: foto(1) },
      { nome: 'Dr. Antonio Ferreira',role: 'Naturopatía · Lisboa',                       iniciais: 'AF', gradient: gradiente(2), foto: foto(2) },
      { nome: 'Dra. Lucía Ramírez',  role: 'Medicina Funcional · Valencia',              iniciais: 'LR', gradient: gradiente(3), foto: foto(3) },
      { nome: 'Dr. Felipe Oliveira', role: 'Nutrología · São Paulo',                     iniciais: 'FO', gradient: gradiente(4), foto: foto(4) },
      { nome: 'Dra. Camila Sánchez', role: 'Bioquímica · Sevilla',                       iniciais: 'CS', gradient: gradiente(5), foto: foto(5) },
      { nome: 'Dr. Javier Morales',  role: 'Fisiología del Deporte · Bogotá',            iniciais: 'JM', gradient: gradiente(6), foto: foto(6) },
      { nome: 'Dra. Patricia Almeida',role: 'Nutricionista · Porto',                     iniciais: 'PA', gradient: gradiente(7), foto: foto(7) },
      { nome: 'Dr. Rafael Cortés',   role: 'Medicina Preventiva · Ciudad de México',     iniciais: 'RC', gradient: gradiente(8), foto: foto(8) },
      { nome: 'Dra. Ana Beltrán',    role: 'PsicoNeuroEndocrinoinmunología · Madrid',    iniciais: 'AB', gradient: gradiente(9), foto: foto(9) }
    ],

    depBadge: 'Testimonios en video',
    depTituloAntes: 'Escucha a los',
    depTituloDestaque: 'profesionales en primera persona',
    depSubtitulo: 'Elige un testimonio en la lista: el video se abre arriba para que puedas verlos todos sin salir de la página.',
    depNavAria: 'Lista de testimonios en video',
    labelAnterior: 'Testimonio anterior',
    labelProximo: 'Siguiente testimonio',
    depoimentos: [
      { videoId: 'kDNl03gcjHM', autor: 'Dr. Carlos Méndez',     role: 'Nutricionista · Madrid' },
      { videoId: 'yBVKrDSyCzs', autor: 'Dra. Sofía Rodríguez',  role: 'Médica Integrativa · Barcelona' },
      { videoId: 'aQwNIvURyxM', autor: 'Centro Bienestar Vital',role: 'Clínica asociada · Barcelona' },
      { videoId: '6zfzdgGk6ac', autor: 'Dr. Javier Morales',    role: 'Medicina del Deporte · Bogotá' },
      { videoId: 'yH9fwjxD9QA', autor: 'Dra. Lucía Ramírez',    role: 'Medicina Funcional · Valencia' },
      { videoId: 'kDNl03gcjHM', autor: 'Dr. Rafael Cortés',     role: 'Medicina Preventiva · CDMX' }
    ],

    ctaBadge: 'Siguiente paso',
    ctaTituloAntes: '¿Quieres ser el',
    ctaTituloDestaque: 'próximo testimonio?',
    ctaSubtitulo: 'Habla con un consultor por WhatsApp y recibe una propuesta pensada para tu consulta o clínica.',
    ctaCardLabel: 'Habla con un consultor',
    ctaCardTitulo: 'Empezar por WhatsApp',
    ctaCardDesc: 'Respondemos en minutos, en horario comercial, sin compromiso.'
  },

  /* ---------------------- PORTUGUÊS ---------------------- */
  pt: {
    heroBadge: 'Depoimentos · Área profissional',
    heroTituloAntes: 'O que dizem',
    heroTituloDestaque: 'quem já usa o OligoCheck',
    heroSubtitulo:
      'Profissionais de saúde do mundo inteiro compartilham como a análise mineral intracelular em 5 segundos mudou a prática no consultório.',
    trust1: '+12.000 profissionais',
    trust2: 'Casos reais verificados',
    trust3: '+10 anos no mercado',

    profBadge: 'Rede OligoCheck',
    profTituloAntes: 'Profissionais que',
    profTituloDestaque: 'confiam no OligoCheck',
    profSubtitulo: 'Um passeio pela comunidade de médicos, nutricionistas e terapeutas que usam o OligoCheck no dia a dia.',
    profAria: 'Carrossel de profissionais que usam OligoCheck',
    profissionais: [
      { nome: 'Dra. Elena Torres',    role: 'Nutricionista · Madrid',                    iniciais: 'ET', gradient: gradiente(0), foto: foto(0) },
      { nome: 'Dr. Marco Rossi',      role: 'Médico Integrativo · Barcelona',            iniciais: 'MR', gradient: gradiente(1), foto: foto(1) },
      { nome: 'Dr. Antônio Ferreira', role: 'Naturopatia · Lisboa',                      iniciais: 'AF', gradient: gradiente(2), foto: foto(2) },
      { nome: 'Dra. Lucía Ramírez',   role: 'Medicina Funcional · Valência',             iniciais: 'LR', gradient: gradiente(3), foto: foto(3) },
      { nome: 'Dr. Felipe Oliveira',  role: 'Nutrologia · São Paulo',                    iniciais: 'FO', gradient: gradiente(4), foto: foto(4) },
      { nome: 'Dra. Camila Sánchez',  role: 'Bioquímica · Sevilha',                      iniciais: 'CS', gradient: gradiente(5), foto: foto(5) },
      { nome: 'Dr. Javier Morales',   role: 'Fisiologia do Esporte · Bogotá',            iniciais: 'JM', gradient: gradiente(6), foto: foto(6) },
      { nome: 'Dra. Patrícia Almeida',role: 'Nutricionista · Porto',                     iniciais: 'PA', gradient: gradiente(7), foto: foto(7) },
      { nome: 'Dr. Rafael Cortés',    role: 'Medicina Preventiva · Cidade do México',    iniciais: 'RC', gradient: gradiente(8), foto: foto(8) },
      { nome: 'Dra. Ana Beltrán',     role: 'PsicoNeuroEndocrinoimunologia · Madrid',    iniciais: 'AB', gradient: gradiente(9), foto: foto(9) }
    ],

    depBadge: 'Depoimentos em vídeo',
    depTituloAntes: 'Escute os',
    depTituloDestaque: 'profissionais em primeira pessoa',
    depSubtitulo: 'Escolha um depoimento na lista: o vídeo abre acima para você assistir a todos sem sair da página.',
    depNavAria: 'Lista de depoimentos em vídeo',
    labelAnterior: 'Depoimento anterior',
    labelProximo: 'Próximo depoimento',
    depoimentos: [
      { videoId: 'kDNl03gcjHM', autor: 'Dr. Carlos Méndez',       role: 'Nutricionista · Madrid' },
      { videoId: 'yBVKrDSyCzs', autor: 'Dra. Sofia Rodríguez',    role: 'Médica Integrativa · Barcelona' },
      { videoId: 'aQwNIvURyxM', autor: 'Centro Bem-Estar Vital',  role: 'Clínica parceira · Barcelona' },
      { videoId: '6zfzdgGk6ac', autor: 'Dr. Javier Morales',      role: 'Medicina do Esporte · Bogotá' },
      { videoId: 'yH9fwjxD9QA', autor: 'Dra. Lucía Ramírez',      role: 'Medicina Funcional · Valência' },
      { videoId: 'kDNl03gcjHM', autor: 'Dr. Rafael Cortés',       role: 'Medicina Preventiva · CDMX' }
    ],

    ctaBadge: 'Próximo passo',
    ctaTituloAntes: 'Quer ser o',
    ctaTituloDestaque: 'próximo depoimento?',
    ctaSubtitulo: 'Fale com um consultor pelo WhatsApp e receba uma proposta pensada para o seu consultório ou clínica.',
    ctaCardLabel: 'Fale com um consultor',
    ctaCardTitulo: 'Começar pelo WhatsApp',
    ctaCardDesc: 'Respondemos em minutos, em horário comercial, sem compromisso.'
  },

  /* ---------------------- ENGLISH ---------------------- */
  en: {
    heroBadge: 'Testimonials · Professional area',
    heroTituloAntes: 'What they say,',
    heroTituloDestaque: 'those already using OligoCheck',
    heroSubtitulo:
      'Health professionals from all over the world share how 5-second intracellular mineral analysis changed the day-to-day of their practice.',
    trust1: '+12,000 professionals',
    trust2: 'Verified real cases',
    trust3: '+10 years in the market',

    profBadge: 'OligoCheck network',
    profTituloAntes: 'Professionals who',
    profTituloDestaque: 'trust OligoCheck',
    profSubtitulo: 'A tour through the community of doctors, nutritionists and therapists using OligoCheck every day.',
    profAria: 'Carousel of professionals using OligoCheck',
    profissionais: [
      { nome: 'Dr. Elena Torres',      role: 'Nutritionist · Madrid',                     iniciais: 'ET', gradient: gradiente(0), foto: foto(0) },
      { nome: 'Dr. Marco Rossi',       role: 'Integrative Medicine · Barcelona',          iniciais: 'MR', gradient: gradiente(1), foto: foto(1) },
      { nome: 'Dr. Antonio Ferreira',  role: 'Naturopathy · Lisbon',                      iniciais: 'AF', gradient: gradiente(2), foto: foto(2) },
      { nome: 'Dr. Lucía Ramírez',     role: 'Functional Medicine · Valencia',            iniciais: 'LR', gradient: gradiente(3), foto: foto(3) },
      { nome: 'Dr. Felipe Oliveira',   role: 'Nutrology · São Paulo',                     iniciais: 'FO', gradient: gradiente(4), foto: foto(4) },
      { nome: 'Dr. Camila Sánchez',    role: 'Biochemistry · Seville',                    iniciais: 'CS', gradient: gradiente(5), foto: foto(5) },
      { nome: 'Dr. Javier Morales',    role: 'Sports Physiology · Bogotá',                iniciais: 'JM', gradient: gradiente(6), foto: foto(6) },
      { nome: 'Dr. Patricia Almeida',  role: 'Nutritionist · Porto',                      iniciais: 'PA', gradient: gradiente(7), foto: foto(7) },
      { nome: 'Dr. Rafael Cortés',     role: 'Preventive Medicine · Mexico City',         iniciais: 'RC', gradient: gradiente(8), foto: foto(8) },
      { nome: 'Dr. Ana Beltrán',       role: 'PsychoNeuroEndocrineImmunology · Madrid',   iniciais: 'AB', gradient: gradiente(9), foto: foto(9) }
    ],

    depBadge: 'Video testimonials',
    depTituloAntes: 'Hear the',
    depTituloDestaque: 'professionals in their own words',
    depSubtitulo: 'Pick a testimonial in the list — the video opens above so you can watch them all without leaving the page.',
    depNavAria: 'List of video testimonials',
    labelAnterior: 'Previous testimonial',
    labelProximo: 'Next testimonial',
    depoimentos: [
      { videoId: 'kDNl03gcjHM', autor: 'Dr. Carlos Méndez',        role: 'Nutritionist · Madrid' },
      { videoId: 'yBVKrDSyCzs', autor: 'Dr. Sofía Rodríguez',      role: 'Integrative Physician · Barcelona' },
      { videoId: 'aQwNIvURyxM', autor: 'Vital Wellness Center',    role: 'Partner clinic · Barcelona' },
      { videoId: '6zfzdgGk6ac', autor: 'Dr. Javier Morales',       role: 'Sports Medicine · Bogotá' },
      { videoId: 'yH9fwjxD9QA', autor: 'Dr. Lucía Ramírez',        role: 'Functional Medicine · Valencia' },
      { videoId: 'kDNl03gcjHM', autor: 'Dr. Rafael Cortés',        role: 'Preventive Medicine · CDMX' }
    ],

    ctaBadge: 'Next step',
    ctaTituloAntes: 'Want to be the',
    ctaTituloDestaque: 'next testimonial?',
    ctaSubtitulo: 'Talk to a consultant on WhatsApp and get a proposal tailored to your practice or clinic.',
    ctaCardLabel: 'Talk to a consultant',
    ctaCardTitulo: 'Start on WhatsApp',
    ctaCardDesc: 'We reply within minutes, during business hours, no commitment.'
  }
}

const t = computed(() => translations[currentLang.value])

/* ============================================================
   SEO — descoberta orgânica (prova social, long-tail em 3 idiomas).
   Inclui:
     · meta básica (title, description, keywords, robots)
     · hreflang em x-default/ES/PT/EN
     · Open Graph + Twitter Card (gerados pelo composable)
     · JSON-LD rico:
         - Organization
         - WebSite (habilita sitelinks search box potential)
         - WebPage (ancora a página no grafo)
         - Product OligoCheck com aggregateRating
         - VideoObject por depoimento (rich snippet de vídeo no Google)
         - Review por depoimento (referencia o VideoObject)
         - ItemList (ordem dos depoimentos)
         - BreadcrumbList (caminho Home > Depoimentos)
   ============================================================ */
const SITE_URL = 'https://portal.oligocheck.es'
const PAGE_PATH = '/testimonios'
const OG_IMAGE =
  'https://socheck.nyc3.cdn.digitaloceanspaces.com/6dce6cd1afe525bd7b8999-logo%20(1).svg'

/* Média e total de avaliações exibidas — ajuste conforme base real. */
const AGGREGATE_RATING = {
  value: 4.9,
  count: 127,
  bestRating: 5,
  worstRating: 1
}

/* Data de publicação genérica usada nos VideoObjects/Reviews. */
const PUBLISH_DATE = '2024-01-15'

const seoContent = {
  es: {
    title: 'Testimonios OligoCheck | Opiniones reales de +12.000 profesionales',
    description:
      'Descubre qué dicen médicos, nutricionistas y terapeutas que ya utilizan OligoCheck en su consulta. Testimonios reales en video sobre el análisis mineral intracelular.',
    keywords: [
      'testimonios oligocheck',
      'opiniones oligocheck',
      'reseñas oligocheck',
      'oligocheck opiniones profesionales',
      'oligocheck médicos',
      'análisis mineral intracelular opiniones',
      'análisis mineral intracelular testimonios',
      'casos reales oligocheck',
      'oligocheck nutricionistas',
      'oligocheck consulta médica'
    ],
    reviewBody: (d) =>
      `${d.autor} (${d.role}) comparte su experiencia real utilizando OligoCheck en la práctica clínica.`,
    videoDesc: (d) =>
      `Testimonio en video de ${d.autor} — ${d.role} — sobre el uso de OligoCheck para análisis mineral intracelular en consulta.`,
    breadcrumbHome: 'Inicio',
    breadcrumbPage: 'Testimonios'
  },
  pt: {
    title: 'Depoimentos OligoCheck | Avaliações reais de +12.000 profissionais',
    description:
      'Veja o que dizem médicos, nutricionistas e terapeutas que já usam o OligoCheck no consultório. Depoimentos reais em vídeo sobre a análise mineral intracelular.',
    keywords: [
      'depoimentos oligocheck',
      'opiniões oligocheck',
      'avaliações oligocheck',
      'oligocheck avaliações profissionais',
      'oligocheck médicos',
      'análise mineral intracelular opiniões',
      'análise mineral intracelular depoimentos',
      'casos reais oligocheck',
      'oligocheck nutricionistas',
      'oligocheck consultório'
    ],
    reviewBody: (d) =>
      `${d.autor} (${d.role}) compartilha sua experiência real usando o OligoCheck na prática clínica.`,
    videoDesc: (d) =>
      `Depoimento em vídeo de ${d.autor} — ${d.role} — sobre o uso do OligoCheck para análise mineral intracelular no consultório.`,
    breadcrumbHome: 'Início',
    breadcrumbPage: 'Depoimentos'
  },
  en: {
    title: 'OligoCheck Testimonials | Real reviews from +12,000 health professionals',
    description:
      'See what doctors, nutritionists and therapists already using OligoCheck say about it. Real video testimonials about intracellular mineral analysis.',
    keywords: [
      'oligocheck testimonials',
      'oligocheck reviews',
      'oligocheck professional reviews',
      'oligocheck doctors reviews',
      'oligocheck nutritionists',
      'intracellular mineral analysis reviews',
      'intracellular mineral analysis testimonials',
      'real oligocheck cases',
      'oligocheck clinic'
    ],
    reviewBody: (d) =>
      `${d.autor} (${d.role}) shares their real experience using OligoCheck in clinical practice.`,
    videoDesc: (d) =>
      `Video testimonial by ${d.autor} — ${d.role} — about using OligoCheck for intracellular mineral analysis in practice.`,
    breadcrumbHome: 'Home',
    breadcrumbPage: 'Testimonials'
  }
}

const canonicalPorLang = {
  es: `${SITE_URL}${PAGE_PATH}`,
  pt: `${SITE_URL}${PAGE_PATH}?lang=pt`,
  en: `${SITE_URL}${PAGE_PATH}?lang=en`
}

useSeo(() => {
  const lang = currentLang.value
  const seo = seoContent[lang] || seoContent.es
  const translation = translations[lang] || translations.es
  const canonical = canonicalPorLang[lang] || canonicalPorLang.es
  const productId = `${SITE_URL}#product-oligocheck`

  /* --------- VideoObject por depoimento (rich snippet forte) --------- */
  const videoObjects = translation.depoimentos.map((d, i) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${canonical}#video-${i + 1}`,
    name: `${translation.depTituloDestaque} — ${d.autor}`,
    description: seo.videoDesc(d),
    thumbnailUrl: [
      `https://i.ytimg.com/vi/${d.videoId}/maxresdefault.jpg`,
      `https://i.ytimg.com/vi/${d.videoId}/hqdefault.jpg`
    ],
    uploadDate: PUBLISH_DATE,
    contentUrl: `https://www.youtube.com/watch?v=${d.videoId}`,
    embedUrl: `https://www.youtube.com/embed/${d.videoId}`,
    publisher: { '@id': `${SITE_URL}#organization` },
    inLanguage: lang,
    isFamilyFriendly: true
  }))

  /* --------- Reviews individuais referenciando os VideoObjects --------- */
  const reviews = translation.depoimentos.map((d, i) => ({
    '@type': 'Review',
    '@id': `${canonical}#review-${i + 1}`,
    author: { '@type': 'Person', name: d.autor, jobTitle: d.role },
    datePublished: PUBLISH_DATE,
    reviewRating: { '@type': 'Rating', ratingValue: 5, bestRating: 5, worstRating: 1 },
    reviewBody: seo.reviewBody(d),
    itemReviewed: { '@id': productId },
    associatedMedia: { '@id': `${canonical}#video-${i + 1}` },
    inLanguage: lang
  }))

  /* --------- ItemList (ordem/navegação) --------- */
  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${canonical}#testimonials`,
    name: translation.depTituloDestaque,
    numberOfItems: translation.depoimentos.length,
    itemListElement: translation.depoimentos.map((_d, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: { '@id': `${canonical}#video-${i + 1}` }
    }))
  }

  /* --------- Product com aggregateRating + reviews embutidos --------- */
  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': productId,
    name: 'OligoCheck',
    description:
      'Dispositivo profesional de análisis mineral intracelular por espectrofotometría — resultados en 5 segundos.',
    brand: { '@type': 'Brand', name: 'OligoCheck' },
    image: OG_IMAGE,
    url: SITE_URL,
    category: 'Medical Device',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: AGGREGATE_RATING.value,
      reviewCount: AGGREGATE_RATING.count,
      bestRating: AGGREGATE_RATING.bestRating,
      worstRating: AGGREGATE_RATING.worstRating
    },
    review: reviews
  }

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${SITE_URL}#organization`,
      name: 'OligoCheck',
      url: SITE_URL,
      logo: OG_IMAGE,
      sameAs: ['https://www.instagram.com/oligocheck/']
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}#website`,
      url: SITE_URL,
      name: 'OligoCheck',
      inLanguage: ['es', 'pt', 'en'],
      publisher: { '@id': `${SITE_URL}#organization` }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: seo.title,
      description: seo.description,
      inLanguage: lang,
      isPartOf: { '@id': `${SITE_URL}#website` },
      primaryImageOfPage: { '@type': 'ImageObject', url: OG_IMAGE },
      about: { '@id': productId }
    },
    product,
    ...videoObjects,
    itemList,
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: seo.breadcrumbHome, item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: seo.breadcrumbPage, item: canonical }
      ]
    }
  ]

  return {
    lang,
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical,
    alternates: {
      'x-default': canonicalPorLang.es,
      es: canonicalPorLang.es,
      pt: canonicalPorLang.pt,
      en: canonicalPorLang.en
    },
    image: OG_IMAGE,
    imageAlt: 'OligoCheck — depoimentos de profissionais de saúde',
    type: 'website',
    structuredData
  }
})
</script>

<style scoped>
/* ============================================================
   TÍTULO do hero — texto que acompanha o gradiente
   ============================================================ */
.titulo-resto { color: var(--ds-text); }

/* ============================================================
   TRUSTBAR dentro do hero
   ============================================================ */
.trust-inline {
  max-width: 720px;
  margin: 0 auto;
}

.trust-inline :deep(.trust-bar) {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}

/* ============================================================
   CARROSSEL DE PROFISSIONAIS — marquee horizontal infinito
   ============================================================ */
.profissionais-header {
  margin: 0 auto var(--ds-s-7);
}

.marquee {
  position: relative;
  width: 100%;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
}

.marquee-track {
  display: flex;
  gap: var(--ds-s-4);
  width: max-content;
  padding: 6px 0;
  animation: marqueeScroll 55s linear infinite;
}

.marquee-track--paused { animation-play-state: paused; }

@keyframes marqueeScroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

.marquee-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 80px;
  pointer-events: none;
  z-index: 1;
}
.marquee-fade--left {
  left: 0;
  background: linear-gradient(90deg, var(--ds-surface-sec), transparent);
}
.marquee-fade--right {
  right: 0;
  background: linear-gradient(270deg, var(--ds-surface-sec), transparent);
}

/* Cartão de profissional (avatar + nome + foto real) */
.prof-card {
  flex-shrink: 0;
  width: 260px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 14px;
  overflow: hidden;
}

.prof-topo {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.prof-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-family: var(--ds-font-bold);
  font-size: 14px;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  box-shadow: 0 8px 24px -8px rgba(20, 203, 240, 0.35);
}

.prof-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.prof-info strong {
  font-family: var(--ds-font-bold);
  font-size: 13.5px;
  color: var(--ds-text);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prof-info span {
  font-size: 11.5px;
  color: var(--ds-text-dim);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prof-foto {
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: var(--ds-r-md);
  background: #0b1a28;
  display: block;
  border: 1px solid var(--ds-border-subtle);
}

/* ============================================================
   PLAYER PRINCIPAL + INFO
   ============================================================ */
.player-principal {
  max-width: 820px;
  margin: 0 auto;
}

.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ds-r-xl);
  overflow: hidden;
  background: #000;
  border: 1px solid var(--ds-border);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(20, 203, 240, 0.1);
}

.video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.player-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--ds-s-3);
  margin-top: var(--ds-s-4);
  padding: 14px 18px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
}

.player-info-ident {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.player-autor {
  font-family: var(--ds-font-bold);
  font-size: 15px;
  color: var(--ds-text);
  line-height: 1.25;
}

.player-role {
  font-size: 12.5px;
  color: var(--ds-text-dim);
  line-height: 1.35;
}

.player-controles {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.player-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ds-text);
  background: var(--ds-surface-3, rgba(255, 255, 255, 0.06));
  border: 1px solid var(--ds-border);
  cursor: pointer;
  font: inherit;
  transition: all var(--ds-t-base);
  padding: 0;
}

.player-btn:hover {
  background: var(--ds-accent);
  border-color: var(--ds-accent);
  color: #06121a;
  transform: scale(1.06);
}

.player-btn:active { transform: scale(0.96); }

.player-btn:focus-visible {
  outline: 2px solid var(--ds-accent);
  outline-offset: 2px;
}

.player-contador {
  font-family: var(--ds-font-bold);
  font-size: 12px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--ds-accent);
  background: var(--ds-accent-soft);
  border: 1px solid var(--ds-accent-border);
  padding: 5px 12px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 56px;
  text-align: center;
}

/* ============================================================
   LISTA DE MINIATURAS
   ============================================================ */
.dep-nav {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: var(--ds-s-3);
  margin-top: var(--ds-s-7);
}

.dep-thumb {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 10px;
  text-align: left;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  transition: all var(--ds-t-smooth);
}

.dep-thumb:hover {
  transform: translateY(-3px);
  border-color: rgba(20, 203, 240, 0.35);
  box-shadow: var(--ds-shadow-lg);
}

.dep-thumb--ativo {
  border-color: var(--ds-accent);
  box-shadow: 0 0 0 3px rgba(20, 203, 240, 0.14), var(--ds-shadow-lg);
  background: linear-gradient(180deg, rgba(20, 203, 240, 0.06), transparent 70%), var(--ds-surface-2);
}

.dep-thumb-img {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ds-r-md);
  overflow: hidden;
  background: #000;
}

.dep-thumb-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform var(--ds-t-smooth);
}

.dep-thumb:hover .dep-thumb-img img,
.dep-thumb--ativo .dep-thumb-img img {
  transform: scale(1.04);
}

.dep-thumb-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  transition: all var(--ds-t-base);
}

.dep-thumb:hover .dep-thumb-play,
.dep-thumb--ativo .dep-thumb-play {
  background: var(--ds-accent);
  border-color: var(--ds-accent);
  transform: translate(-50%, -50%) scale(1.08);
}

.dep-thumb-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 4px 6px;
  min-width: 0;
}

.dep-thumb-info strong {
  font-family: var(--ds-font-bold);
  font-size: 14px;
  color: var(--ds-text);
  line-height: 1.25;
}

.dep-thumb-info span {
  font-size: 12px;
  color: var(--ds-text-dim);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================================================
   CTA FINAL (WhatsApp)
   ============================================================ */
.cta-bloco {
  max-width: 560px;
  margin: 0 auto;
}

.caminho-card {
  display: flex;
  align-items: center;
  gap: var(--ds-s-4);
  text-align: left;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 22px;
  text-decoration: none;
  color: var(--ds-text);
  transition: all var(--ds-t-smooth);
  position: relative;
  overflow: hidden;
}

.caminho-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity var(--ds-t-base);
  pointer-events: none;
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.16), transparent 60%);
}

.caminho-card:hover::before { opacity: 1; }

.caminho-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--ds-shadow-lg);
  border-color: rgba(37, 211, 102, 0.5);
}

.caminho-icone {
  min-width: 56px;
  height: 56px;
  border-radius: var(--ds-r-md);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  color: #fff;
}

.icone-cta {
  background: var(--ds-gradient-success);
  box-shadow: var(--ds-shadow-glow-success);
}

.caminho-conteudo {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  position: relative;
  z-index: 1;
  min-width: 0;
}

.caminho-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--ds-text-dim);
  font-family: var(--ds-font-bold);
}

.caminho-titulo {
  font-family: var(--ds-font-bold);
  font-size: 17px;
  color: var(--ds-text);
  line-height: 1.25;
}

.caminho-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.45;
  margin-top: 2px;
}

.caminho-seta {
  color: var(--ds-text-faint);
  transition: all var(--ds-t-base);
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.caminho-card:hover .caminho-seta {
  color: var(--ds-text);
  transform: translateX(4px);
}

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 720px) {
  .marquee-fade { width: 40px; }

  .prof-card { width: 220px; padding: 12px; gap: 10px; }
  .prof-avatar { width: 38px; height: 38px; font-size: 13px; }
  .prof-info strong { font-size: 12.5px; }
  .prof-info span { font-size: 11px; }

  .video-wrapper { border-radius: var(--ds-r-lg); }

  .player-info {
    padding: 12px 14px;
  }

  .player-btn { width: 34px; height: 34px; }

  /* ----------------------------------------------------------
     Lista de miniaturas vira scroll horizontal com snap
     (no mobile, scroll vertical pela página ficava cansativo).
     ---------------------------------------------------------- */
  .dep-nav {
    display: flex;
    grid-template-columns: none;
    gap: 12px;
    margin-top: var(--ds-s-5);
    margin-left: calc(var(--ds-s-5) * -1);
    margin-right: calc(var(--ds-s-5) * -1);
    padding: 4px var(--ds-s-5) 10px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: var(--ds-s-5);
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .dep-nav::-webkit-scrollbar { display: none; }

  .dep-thumb {
    flex: 0 0 200px;
    scroll-snap-align: center;
  }

  .dep-thumb-info strong { font-size: 13px; }
  .dep-thumb-info span { font-size: 11.5px; }

  .caminho-card { padding: 18px; gap: var(--ds-s-3); }
  .caminho-icone { min-width: 48px; height: 48px; }
  .caminho-titulo { font-size: 16px; }
  .caminho-desc { font-size: 12.5px; }
}

@media (max-width: 480px) {
  .player-info {
    flex-wrap: wrap;
    gap: 12px;
  }
  .player-info-ident { flex: 1 1 100%; }
  .player-controles {
    width: 100%;
    justify-content: space-between;
  }

  .dep-thumb { flex: 0 0 180px; }
}
</style>
